'use client';

import { useScheduler } from '../SchedulerContext';
import InternalStepper, {
  InternalStep,
  InternalStepperRef,
} from '../components/InternalStepper';
import ServiceFilterForm from './ServiceFilterForm';
import SelectPrimaryService from './SelectPrimaryService';
import SelectAdditionalServices from './SelectAdditionalServices';
import SearchServices from './SearchServices';
import ContactForm from './ContactForm';
import { useEffect, useRef, useState, useMemo } from 'react';
import {
  Bundle,
  Service,
  ContactRoleType,
  ServWithMods,
} from '@/util/types/serverTypes';
import { InternalStepValidationProvider } from '../context/InternalStepValidationContext';
import { useStartOver } from '../hooks/useStartOver';
import { prefillNeedsPrimaryServiceSearch } from '../utils/schedulerPrefill';
import { FilterOption, PathStep } from '@/util/types/serverTypeCollection/serviceFilter';

type ServicesStepProps = {
  services: ServWithMods[];
  bundles: Bundle[];
  contactRoles: ContactRoleType[];
};

export default function ServicesStep({
  services,
  bundles,
  contactRoles,
}: ServicesStepProps) {
  const {
    selectedRole,
    setSelectedRole,
    selectedPrimaryService,
    setSelectedPrimaryService,
    selectedRoleIds,
    setSelectedRoleIds,
    serviceFilter,
    prefill,
  } = useScheduler();
  const internalStepperRef = useRef<InternalStepperRef>(null);
  const [filteredServiceIds, setFilteredServiceIds] = useState<string[]>([]);
  const [servicesFilteredList, setServicesFilteredList] = useState<
    ServWithMods[]
  >([]);
  const [filterFormPath, setFilterFormPath] = useState<PathStep[]>([]);

  // Check if there are valid questions configured
  // Use the same logic as ServiceFilterForm to determine valid options
  const hasValidQuestions = useMemo(() => {
    const hasTerminalDescendant = (node: FilterOption): boolean => {
      // If node is inactive, exclude it and all its children
      if (node.active === false) {
        return false;
      }

      if (
        node.accumulatedServiceIds &&
        node.accumulatedServiceIds.length > 0 &&
        node.accumulatedRoleIds &&
        node.accumulatedRoleIds.length > 0
      ) {
        return true;
      }
      if (!node.subOptions || node.subOptions.length === 0) {
        return false;
      }
      return node.subOptions.some((child) => hasTerminalDescendant(child));
    };

    const filterValidOptions = (options: FilterOption[]): FilterOption[] => {
      return options
        .filter((opt) => opt.active !== false && hasTerminalDescendant(opt))
        .map((opt) => ({
          ...opt,
          subOptions: opt.subOptions
            ? filterValidOptions(opt.subOptions)
            : undefined,
        }));
    };

    const validRootOptions = filterValidOptions(
      serviceFilter.options.filter((opt) => !opt.parentId)
    );

    return validRootOptions.length > 0;
  }, [serviceFilter.options]);

  // Default to search mode if no questions are configured, or when a scheduling
  // link asked for a primary service we could not honor: those links skip the
  // questionnaire, so the filtered primary-service list would be empty.
  const [isSearchMode, setIsSearchMode] = useState(
    () => !hasValidQuestions || prefillNeedsPrimaryServiceSearch(prefill)
  );

  // Sync search mode when questions availability changes
  useEffect(() => {
    // If no valid questions exist, default to search mode
    // Only update if we're not already in the correct state
    if (!hasValidQuestions && !isSearchMode) {
      setIsSearchMode(true);
    }
  }, [hasValidQuestions, isSearchMode]);

  // Calculate initial step based on context data (for draft rehydration)
  // This MUST be reactive to context so it recalculates when DraftLoader populates data
  const initialStep = useMemo(() => {
    if (hasValidQuestions) {
      // With questions: Step 0 = Filter Form, Step 1 = Search/Select, Step 2 = Contact
      // Step 0: Service Filter Form - if no role selected
      if (!selectedRole && !selectedPrimaryService) {
        console.log(
          '[ServicesStep] Initial step: 0 (Filter Form) - no role or service selected'
        );
        return 0;
      }

      // Step 1: Select Primary Service - if role selected but no primary service
      if (selectedRole && !selectedPrimaryService) {
        console.log(
          '[ServicesStep] Initial step: 1 (Select Primary) - has role, no primary service'
        );
        return 1;
      }

      // Step 2: Contact Form - if primary service selected
      if (selectedPrimaryService) {
        console.log(
          '[ServicesStep] Initial step: 2 (Contact Form) - has primary service'
        );
        return 2;
      }

      // Default to first step
      console.log('[ServicesStep] Initial step: 0 (default)');
      return 0;
    } else {
      // Without questions: Step 0 = Search/Select, Step 1 = Contact
      // Step 0: Search/Select - if no primary service selected
      if (!selectedPrimaryService) {
        console.log(
          '[ServicesStep] Initial step: 0 (Search/Select) - no primary service selected, no questions'
        );
        return 0;
      }

      // Step 1: Contact Form - if primary service selected
      if (selectedPrimaryService) {
        console.log(
          '[ServicesStep] Initial step: 1 (Contact Form) - has primary service, no questions'
        );
        return 1;
      }

      // Default to first step
      console.log('[ServicesStep] Initial step: 0 (default, no questions)');
      return 0;
    }
  }, [selectedRole, selectedPrimaryService, hasValidQuestions, isSearchMode]); // React to context changes!

  const handleFilterComplete = (result: {
    serviceIds: string[];
    roleIds: string[];
  }) => {
    setFilteredServiceIds(result.serviceIds);
    setSelectedRoleIds(result.roleIds);
    // Don't clear path - preserve it so user can go back to last question
  };

  const handleStartOver = useStartOver({
    onBeforeReset: () => {
      // Clear step-specific local state
      setFilteredServiceIds([]);
      setServicesFilteredList([]);
      setFilterFormPath([]);
      // Reset to default search mode based on questions availability
      setIsSearchMode(!hasValidQuestions);

      // Clear role-related state
      setSelectedRole(null);
      setSelectedRoleIds([]);
      setSelectedPrimaryService(null);

      // Navigate back to the first internal step
      if (internalStepperRef.current) {
        internalStepperRef.current.setActiveStep(0);
      }
    },
  });

  const handleSearchMode = () => {
    // When user chooses to search, satisfy step 0 validation by setting role IDs
    // This allows navigation to step 1 in search mode
    // Use empty array to indicate "search mode bypass"
    if (
      (!selectedRoleIds || selectedRoleIds.length === 0) &&
      hasValidQuestions
    ) {
      setSelectedRoleIds(['search-mode']); // Placeholder to satisfy validation
    }

    setIsSearchMode(true);

    // Explicitly navigate to step 1 when search mode is enabled
    // Step 1 will show SearchServices when isSearchMode is true
    if (hasValidQuestions && internalStepperRef.current) {
      internalStepperRef.current.setActiveStep(1);
    }
  };

  const handleBackFromSearch = () => {
    setIsSearchMode(false);
    setSelectedPrimaryService(null);
    // Clear search-mode placeholder if it exists
    if (selectedRoleIds && selectedRoleIds.includes('search-mode')) {
      setSelectedRoleIds([]);
    }
    // Go back to filter form only if questions exist
    if (hasValidQuestions && internalStepperRef.current) {
      internalStepperRef.current.setActiveStep(0);
    }
  };

  const handleServiceSelectedFromSearch = () => {
    // When a service is selected from search, advance to contact form
    // Step index depends on whether questions are configured
    const contactFormStep = hasValidQuestions ? 2 : 1;
    if (internalStepperRef.current) {
      internalStepperRef.current.setActiveStep(contactFormStep);
    }
  };

  // Auto-advance to review step when services are filtered (not in search mode)
  useEffect(() => {
    if (
      filteredServiceIds.length > 0 &&
      !isSearchMode &&
      internalStepperRef.current
    ) {
      internalStepperRef.current.setActiveStep(1);
    }
  }, [filteredServiceIds, isSearchMode]);

  // Auto-advance to search step when search mode is enabled (only if questions exist)
  useEffect(() => {
    // Only auto-advance if we have questions and user manually switched to search
    if (isSearchMode && hasValidQuestions && internalStepperRef.current) {
      internalStepperRef.current.setActiveStep(1);
    }
  }, [isSearchMode, hasValidQuestions]);

  const handleNextToAdditionalServices = () => {
    // Advance to contact form - step index depends on whether questions are configured
    const contactFormStep = hasValidQuestions ? 2 : 1;
    if (internalStepperRef.current) {
      internalStepperRef.current.setActiveStep(contactFormStep);
    }
  };

  const handleBackFromSelectPrimary = () => {
    // Go back to filter form (step 0) only if questions are configured
    if (hasValidQuestions && internalStepperRef.current) {
      // Clear only the terminal selection (filteredServiceIds) to unlock the form
      // But preserve the path so user resumes at the last question
      setFilteredServiceIds([]);
      setSelectedPrimaryService(null);
      // Don't clear selectedRoleIds or filterFormPath - preserve navigation state
      internalStepperRef.current.setActiveStep(0);
    }
  };

  // Build steps array conditionally based on whether questions are configured
  // When no questions: skip the filter form step and start directly with search/select
  const steps: InternalStep[] = hasValidQuestions
    ? [
        // Step 0: Filter form (only when questions are configured)
        {
          id: 'service_questions',
          label: 'What Are You Looking For?',
          content: (
            <ServiceFilterForm
              onCompleteAction={handleFilterComplete}
              isDisabled={filteredServiceIds.length > 0}
              onSearch={handleSearchMode}
              path={filterFormPath}
              setPath={setFilterFormPath}
            />
          ),
        },
        // Step 1: Search or Select Primary Service
        {
          id: isSearchMode ? 'service_search' : 'service_list',
          label: isSearchMode
            ? 'Search for Services'
            : 'Select A Primary Service',
          content: isSearchMode ? (
            <SearchServices
              allServices={services}
              bundles={bundles}
              onBack={handleBackFromSearch}
              onServiceSelected={handleServiceSelectedFromSearch}
              showBackButton={hasValidQuestions}
            />
          ) : (
            <SelectPrimaryService
              filteredServiceIds={filteredServiceIds}
              allServices={services}
              bundles={bundles}
              onNext={handleNextToAdditionalServices}
              onStartOver={handleStartOver}
              onSearch={handleSearchMode}
              onBack={hasValidQuestions ? handleBackFromSelectPrimary : undefined}
              servicesFilteredList={servicesFilteredList}
              setServicesFilteredList={setServicesFilteredList}
            />
          ),
        },
        // Step 2: Contact Form
        {
          id: 'contact_info',
          label: 'Tell Us About Yourself',
          content: <ContactForm hasValidQuestions={hasValidQuestions} />,
        },
      ]
    : [
        // When no questions configured: start directly with search/select (no filter step)
        {
          id: isSearchMode ? 'service_search' : 'service_list',
          label: isSearchMode
            ? 'Search for Services'
            : 'Select A Primary Service',
          content: isSearchMode ? (
            <SearchServices
              allServices={services}
              bundles={bundles}
              onBack={handleBackFromSearch}
              onServiceSelected={handleServiceSelectedFromSearch}
              showBackButton={false}
            />
          ) : (
            <SelectPrimaryService
              filteredServiceIds={filteredServiceIds}
              allServices={services}
              bundles={bundles}
              onNext={handleNextToAdditionalServices}
              onStartOver={handleStartOver}
              onSearch={handleSearchMode}
              onBack={undefined}
              servicesFilteredList={servicesFilteredList}
              setServicesFilteredList={setServicesFilteredList}
            />
          ),
        },
        // Step 1: Contact Form
        {
          id: 'contact_info',
          label: 'Tell Us About Yourself',
          content: <ContactForm hasValidQuestions={hasValidQuestions} />,
        },
      ];

  return (
    <div className='min-h-[600px]'>
      <InternalStepValidationProvider
        mainStep={1}
        totalSteps={steps.length}
        hasFilterStep={hasValidQuestions}
      >
        <InternalStepper
          ref={internalStepperRef}
          steps={steps}
          initialStep={initialStep}
          onStartOver={handleStartOver}
          trackingStep='services'
        />
      </InternalStepValidationProvider>
    </div>
  );
}
